import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { formatAppName } from '../lib/appName';
import type { InfrastructureApp } from '../types';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs } from './ui/tabs';

// Zod schema for form validation
const appEditSchema = z.object({
  environments: z.array(z.enum(['dev', 'prod'])),
  bulkDeploymentExclusions: z.array(z.enum(['dev', 'prod'])),
  devInfo: z.string().optional(),
  devTriggerUrls: z.string().optional(),
  devLogsUrls: z.string().optional(),
  prodInfo: z.string().optional(),
  prodTriggerUrls: z.string().optional(),
  prodLogsUrls: z.string().optional(),
});

type AppEditFormData = z.infer<typeof appEditSchema>;

interface AppEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: InfrastructureApp;
  onSave: (
    appName: string,
    environments: string[],
    validationInstructions?: Record<string, { info?: string }>,
    bulkDeploymentExclusions?: string[],
  ) => Promise<void>;
}

export const AppEditDialog = ({ open, onOpenChange, app, onSave }: AppEditDialogProps) => {
  const [activeTab, setActiveTab] = useState<'dev' | 'prod'>('dev');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<AppEditFormData>({
    resolver: zodResolver(appEditSchema),
    defaultValues: {
      environments: [],
      bulkDeploymentExclusions: [],
      devInfo: '',
      devTriggerUrls: '',
      devLogsUrls: '',
      prodInfo: '',
      prodTriggerUrls: '',
      prodLogsUrls: '',
    },
  });

  const environments = watch('environments');
  const bulkDeploymentExclusions = watch('bulkDeploymentExclusions');

  // Reset form when app changes
  useEffect(() => {
    if (app) {
      const envs = app.environments
        .split(',')
        .map((e) => e.trim())
        .filter((e): e is 'dev' | 'prod' => e === 'dev' || e === 'prod');

      reset({
        environments: envs,
        bulkDeploymentExclusions: (app.bulkDeploymentExclusions || []).filter(
          (environment): environment is 'dev' | 'prod' =>
            environment === 'dev' || environment === 'prod',
        ),
        devInfo: app.validationInstructions?.dev?.info || '',
        devTriggerUrls: app.validationInstructions?.dev?.trigger?.join('\n') || '',
        devLogsUrls: app.validationInstructions?.dev?.logs?.join('\n') || '',
        prodInfo: app.validationInstructions?.prod?.info || '',
        prodTriggerUrls: app.validationInstructions?.prod?.trigger?.join('\n') || '',
        prodLogsUrls: app.validationInstructions?.prod?.logs?.join('\n') || '',
      });
    }
  }, [app, reset]);

  const handleToggleEnvironment = (env: 'dev' | 'prod') => {
    const current = environments || [];
    const updated = current.includes(env) ? current.filter((e) => e !== env) : [...current, env];
    setValue('environments', updated);
  };

  const handleToggleBulkDeploymentExclusion = (env: 'dev' | 'prod') => {
    const current = bulkDeploymentExclusions || [];
    const updated = current.includes(env)
      ? current.filter((environment) => environment !== env)
      : [...current, env];
    setValue('bulkDeploymentExclusions', updated);
  };

  // Helper function to parse URLs from textarea
  const parseUrls = (text: string | undefined): string[] | undefined => {
    if (!text) return undefined;
    const urls = text
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
    return urls.length > 0 ? urls : undefined;
  };

  const onSubmit = async (data: AppEditFormData) => {
    try {
      const validationInstructions: Record<
        string,
        { info?: string; trigger?: string[]; logs?: string[] }
      > = {};

      // Dev environment
      const devInfo = data.devInfo?.trim() || undefined;
      const devTrigger = parseUrls(data.devTriggerUrls);
      const devLogs = parseUrls(data.devLogsUrls);
      if (devInfo || devTrigger || devLogs) {
        validationInstructions.dev = {
          info: devInfo,
          trigger: devTrigger,
          logs: devLogs,
        };
      }

      // Prod environment
      const prodInfo = data.prodInfo?.trim() || undefined;
      const prodTrigger = parseUrls(data.prodTriggerUrls);
      const prodLogs = parseUrls(data.prodLogsUrls);
      if (prodInfo || prodTrigger || prodLogs) {
        validationInstructions.prod = {
          info: prodInfo,
          trigger: prodTrigger,
          logs: prodLogs,
        };
      }

      await onSave(
        app.name,
        data.environments,
        validationInstructions,
        data.bulkDeploymentExclusions,
      );
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save app:', error);
      // TODO: Show error message to user
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-2xl'>
        <DialogHeader>
          <DialogTitle>{formatAppName(app.name)}</DialogTitle>
          <DialogDescription>Configure the environments for this application</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 mt-6'>
          <Tabs
            tabs={[
              {
                id: 'dev',
                label: 'Development',
                content: (
                  <div className='space-y-3'>
                    <label className='flex items-center gap-3 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={environments?.includes('dev') || false}
                        onChange={() => handleToggleEnvironment('dev')}
                        className='w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer'
                      />
                      <span className='text-sm text-gray-900 font-medium'>Enabled</span>
                    </label>
                    <label className='flex items-start gap-3 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={bulkDeploymentExclusions?.includes('dev') || false}
                        onChange={() => handleToggleBulkDeploymentExclusion('dev')}
                        className='mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer'
                      />
                      <span className='text-sm text-gray-900'>
                        <span className='font-medium'>Exclude from bulk deployment actions</span>
                        <span className='mt-0.5 block text-xs text-gray-500'>
                          Use this for critical deployments that must be selected manually.
                        </span>
                      </span>
                    </label>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Validation Instructions
                      </label>
                      <textarea
                        {...register('devInfo')}
                        placeholder='e.g., Check the job execution logs in dev environment'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary'
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Trigger URLs (one per line)
                      </label>
                      <textarea
                        {...register('devTriggerUrls')}
                        placeholder='https://example.com/trigger/dev'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary font-mono'
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Logs URLs (one per line)
                      </label>
                      <textarea
                        {...register('devLogsUrls')}
                        placeholder='https://example.com/logs/dev'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary font-mono'
                        rows={2}
                      />
                    </div>
                  </div>
                ),
              },
              {
                id: 'prod',
                label: 'Production',
                content: (
                  <div className='space-y-3'>
                    <label className='flex items-center gap-3 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={environments?.includes('prod') || false}
                        onChange={() => handleToggleEnvironment('prod')}
                        className='w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer'
                      />
                      <span className='text-sm text-gray-900 font-medium'>Enabled</span>
                    </label>
                    <label className='flex items-start gap-3 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={bulkDeploymentExclusions?.includes('prod') || false}
                        onChange={() => handleToggleBulkDeploymentExclusion('prod')}
                        className='mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer'
                      />
                      <span className='text-sm text-gray-900'>
                        <span className='font-medium'>Exclude from bulk deployment actions</span>
                        <span className='mt-0.5 block text-xs text-gray-500'>
                          Use this for critical deployments that must be selected manually.
                        </span>
                      </span>
                    </label>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Validation Instructions
                      </label>
                      <textarea
                        {...register('prodInfo')}
                        placeholder='e.g., Verify job completed successfully in production logs'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary'
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Trigger URLs (one per line)
                      </label>
                      <textarea
                        {...register('prodTriggerUrls')}
                        placeholder='https://example.com/trigger/prod'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary font-mono'
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className='text-sm text-gray-600 mb-2 block'>
                        Logs URLs (one per line)
                      </label>
                      <textarea
                        {...register('prodLogsUrls')}
                        placeholder='https://example.com/logs/prod'
                        className='w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 focus:ring-primary focus:border-primary font-mono'
                        rows={2}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => setActiveTab(tabId as 'dev' | 'prod')}
          />
        </form>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type='submit' onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
